import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Eye,
  Pencil,
  MoreHorizontal,
  Megaphone,
  LineChart,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { convertToZone, formatDateToShort } from "../../utils/formatters";
import { getProductDetail } from "../../services/store/productAxios";
import { Product, ProductType } from "../../interfaces/websites";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { database } from "../../lib/firebase";
import { ref, onValue } from "firebase/database";
import { useRealtimeEnvironment } from "../../hooks/useRealtimeEnvironment";
import { updateFunnelName } from "../../services/funnels";
import { invalidateWebsitesCache } from "../../store/websitesStore";

interface FunnelsCardProps {
  id: string;
  idHtml: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  product: Product[];
  idSequence: number;
  handleDuplicate: () => void;
  handleEdit: () => void;
  handleOpenModal: () => void;
  handlePreview: () => void;
  handleAdsClick: () => void;
  sectionsGenerated: boolean | undefined;
  hasErrors?: boolean;
  errorSections?: string[];
  generationStatus?: string | null;
  failureReason?: string | null;
  retryAvailable?: boolean;
  onRetry?: () => void | Promise<void>;
  configurations: {
    enabled?: boolean;
    name_funnel?: string | null;
    [key: string]: unknown;
  };
  nameFunnel: string;
  productName?: string;
  productImage?: string;
  salesSummary?: {
    salesCount: number;
    revenuePaid: number | string;
    currencyCode?: string | null;
    conversionRate?: number | string | null;
  } | null;
}

const MAX_FUNNEL_NAME_WORDS = 5;
const FUNNEL_NAME_TRAILING_JOINERS = new Set([
  "de",
  "del",
  "la",
  "las",
  "el",
  "los",
  "para",
  "por",
  "con",
  "sin",
  "que",
  "y",
  "o",
]);

const normalizeFunnelName = (value: string) => {
  const words = value
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean)
    .slice(0, MAX_FUNNEL_NAME_WORDS);

  while (
    words.length > 1 &&
    FUNNEL_NAME_TRAILING_JOINERS.has(words[words.length - 1].toLowerCase().replace(/[.,:;]+$/g, ""))
  ) {
    words.pop();
  }

  return words.join(" ");
};

const formatCompactCurrency = (value: number | string, currencyCode?: string | null) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "$0";

  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: currencyCode || "COP",
      maximumFractionDigits: 0,
      notation: amount >= 1_000_000 ? "compact" : "standard",
    }).format(amount);
  } catch {
    return `$${Math.round(amount).toLocaleString("es-CO")}`;
  }
};

const parseConversionRate = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return null;
  const rate = Number(value || 0);
  if (!Number.isFinite(rate)) return null;
  return rate;
};

const formatConversionRate = (value: number) => {
  const rate = Number(value || 0);
  return `${rate.toFixed(rate >= 10 ? 0 : 1)}%`;
};

export const FunnelsCard: React.FC<FunnelsCardProps> = ({
  id,
  idHtml,
  description,
  isActive,
  createdAt,
  product,
  idSequence,
  handleDuplicate,
  handleEdit,
  handleOpenModal,
  handlePreview,
  handleAdsClick,
  sectionsGenerated,
  hasErrors,
  errorSections,
  generationStatus,
  failureReason,
  retryAvailable,
  onRetry,
  configurations,
  nameFunnel,
  productName,
  productImage,
  salesSummary,
}) => {
  const { language } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showControls, setShowControls] = useState(Boolean(sectionsGenerated));
  const [isRetrying, setIsRetrying] = useState(false);
  // has_errors is severity-gated in the backend (3+ qualified errors). When
  // the backend marks it true the funnel is broken enough to need a retry
  // before the user can use it, regardless of whether build finished — so
  // the CTA replaces the card whether sectionsGenerated is true or not.
  const failed = Boolean(hasErrors || generationStatus === "failed");
  const recovering = generationStatus === "recovering";
  const isLocalDemoFunnel = Boolean(configurations.__local_html_demo_funnel);
  const [image, setImage] = useState<string>(productImage || "");
  const [fetchedProductName, setFetchedProductName] = useState<string>(productName || "");
  const externalConfiguredName = normalizeFunnelName(configurations.name_funnel || "");
  const [editableName, setEditableName] = useState<string>(externalConfiguredName);
  const [draftName, setDraftName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const realtimeEnvironment = useRealtimeEnvironment();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const skipNameBlurSaveRef = useRef(false);
  const nameCommitInFlightRef = useRef(false);

  const configuredDisplayName = editableName || externalConfiguredName;
  const displayName = configuredDisplayName || normalizeFunnelName(nameFunnel) || dictionary["funnel"][language];
  const funnelSequenceLabel = idSequence > 0 ? `${dictionary["funnel"][language]} #${idSequence}` : "";
  const hasSalesSummary = Boolean(salesSummary && salesSummary.salesCount > 0);
  const conversionRate = parseConversionRate(salesSummary?.conversionRate);
  const hasConversionRate = conversionRate !== null;

  useEffect(() => {
    if (sectionsGenerated) {
      setShowControls(true);
    }
  }, [sectionsGenerated]);

  useEffect(() => {
    if (productImage) {
      setImage(productImage);
    }
    if (productName) {
      setFetchedProductName(productName);
    }
  }, [productImage, productName]);

  useEffect(() => {
    if (isEditingName) return;
    setEditableName(externalConfiguredName);
  }, [externalConfiguredName, id]);

  useEffect(() => {
    if (!isEditingName) return;
    nameInputRef.current?.focus();
    nameInputRef.current?.select();
  }, [isEditingName]);

  const startNameEdit = useCallback(() => {
    setNameError(null);
    setDraftName(displayName);
    setIsEditingName(true);
  }, [displayName]);

  const cancelNameEdit = useCallback(() => {
    skipNameBlurSaveRef.current = true;
    setNameError(null);
    setDraftName(displayName);
    setIsEditingName(false);
    window.setTimeout(() => {
      skipNameBlurSaveRef.current = false;
    }, 0);
  }, [displayName]);

  const commitNameEdit = useCallback(async () => {
    if (nameCommitInFlightRef.current) return;

    const nextName = normalizeFunnelName(draftName);
    if (!nextName) {
      cancelNameEdit();
      return;
    }

    const currentName = normalizeFunnelName(displayName);
    if (nextName === currentName) {
      setDraftName(currentName);
      setIsEditingName(false);
      return;
    }

    nameCommitInFlightRef.current = true;
    setIsSavingName(true);
    setNameError(null);

    try {
      const updatedWebsite = await updateFunnelName(id, nextName, configurations);
      invalidateWebsitesCache();
      const resolvedName = normalizeFunnelName(
        updatedWebsite?.configurations?.name_funnel || nextName
      );
      setEditableName(resolvedName);
      setDraftName(resolvedName);
      setIsEditingName(false);
    } catch (error) {
      console.error("Error updating funnel name:", error);
      setNameError(dictionary["could-not-save"]?.[language] ?? "Could not save");
      nameInputRef.current?.focus();
    } finally {
      nameCommitInFlightRef.current = false;
      setIsSavingName(false);
    }
  }, [cancelNameEdit, configurations, displayName, draftName, id, language]);

  const handleNameBlur = useCallback(() => {
    if (skipNameBlurSaveRef.current) {
      skipNameBlurSaveRef.current = false;
      return;
    }
    void commitNameEdit();
  }, [commitNameEdit]);

  const handleNameKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        void commitNameEdit();
      }
      if (event.key === "Escape") {
        event.preventDefault();
        cancelNameEdit();
      }
    },
    [cancelNameEdit, commitNameEdit]
  );

  const fetchProductInfo = useCallback((force = false) => {
    if (!force && productImage && productName) return;
    const principalProduct = product.find(
      (p) => p.type === ProductType.PRINCIPAL
    );
    if (principalProduct) {
      getProductDetail(principalProduct.product_id)
        .then((product) => {
          const fallbackImage = Array.isArray(product.images)
            ? product.images.find(Boolean)
            : "";
          setImage(product.base_image_url || product.best_image_url || fallbackImage || productImage || "");
          if (product.name || productName) setFetchedProductName(product.name || productName || "");
        })
        .catch((error) => {
          console.error("Error fetching product detail:", error);
        });
    }
  }, [product, productImage, productName]);

  useEffect(() => {
    fetchProductInfo();
  }, [fetchProductInfo, id]);

  // Re-fetch product image when sections finish generating
  useEffect(() => {
    if (showControls && !image && !productImage) {
      fetchProductInfo();
    }
  }, [fetchProductInfo, showControls, image, productImage]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  useEffect(() => {
    if (showControls || !realtimeEnvironment) return;
    const dataRef = ref(
      database,
      `${realtimeEnvironment}/websites/${id}/sections-generated`
    );
    const unsubscribe = onValue(dataRef, (snapshot) => {
      const value = snapshot.val();
      if (typeof value?.done === "boolean") {
        setShowControls(value.done);
        if (value.done) {
          fetchProductInfo(true);
        }
      }
      if (sectionsGenerated) {
        setShowControls(true);
        fetchProductInfo(true);
      }
    });
    return () => unsubscribe();
  }, [fetchProductInfo, id, realtimeEnvironment, sectionsGenerated, showControls]);

  return (
    <div
      id={idHtml}
      className={`flex flex-col justify-between rounded-[20px] shadow-sm border border-gray-200 overflow-hidden transition-all duration-500 ${!showControls ? "bg-white/70" : "bg-white"
        }`}
    >
      <div className="p-6">
        <div className="flex flex-col items-start gap-3 border-b border-gray-100 pb-4 xl:flex-row xl:items-start">
          <div className="relative w-[84px] h-[84px] rounded-lg shadow-sm border border-gray-200 overflow-hidden flex-shrink-0">
            <img
              src={image || "/assets/images/product-placeholder.png"}
              alt={productName || displayName}
              className="w-full h-full object-cover"
            />
            {!image && (
              <div className="absolute inset-0 overflow-hidden">
                <div
                  className="absolute inset-0 animate-shine"
                  style={{
                    background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%)',
                    width: '200%',
                    height: '100%',
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 w-full">
            <div className="flex w-full items-start justify-between gap-2">
              <div className="flex flex-1 flex-col min-w-0 w-full">
                {failed ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-700 mb-1 self-start">
                    {dictionary["funnel-failed"][language]}
                  </span>
                ) : recovering ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 mb-1 self-start animate-pulse">
                    {dictionary["processing"][language]}
                  </span>
                ) : !showControls ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 mb-1 self-start animate-pulse">
                    {dictionary["processing"][language]}
                  </span>
                ) : (
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${isActive
                      ? "bg-[#DCFCE7] text-[#15803D]"
                      : "bg-gray-100 text-gray-600"
                      } mb-1 self-start`}
                  >
                    {isActive && (
                      <span className="w-1 h-1 rounded-full bg-[#15803D] mr-1"></span>
                    )}
                    {isActive
                      ? dictionary["active"][language]
                      : dictionary["draft"][language]}
                  </span>
                )}
                  <div className="group/name flex w-full min-w-0 items-start gap-1.5">
                    {isEditingName ? (
                      <div className="w-full min-w-0">
                        <input
                          ref={nameInputRef}
                          value={draftName}
                          disabled={isSavingName}
                          onChange={(event) => {
                            setDraftName(event.target.value);
                            setNameError(null);
                          }}
                          onBlur={handleNameBlur}
                          onKeyDown={handleNameKeyDown}
                          aria-label="Nombre del embudo"
                          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-left text-[1rem] font-semibold leading-[1.35rem] text-gray-900 outline-none transition focus:border-gray-500 focus:ring-2 focus:ring-gray-100 disabled:opacity-70"
                        />
                        {nameError && (
                          <p className="mt-1 text-[11px] leading-4 text-red-500">
                            {nameError}
                          </p>
                        )}
                      </div>
                    ) : (
                      <h3 className="min-w-0 flex-1 line-clamp-2 text-[1rem] leading-[1.4rem] font-semibold text-gray-900">
                        {displayName}
                      </h3>
                    )}
                    {!isEditingName && !isLocalDemoFunnel && (
                      <button
                        type="button"
                        onClick={startNameEdit}
                        aria-label="Editar nombre del embudo"
                        title="Editar nombre"
                        className="mt-0.5 rounded-full p-1 text-gray-400 opacity-0 transition hover:bg-gray-100 hover:text-gray-700 focus:bg-gray-100 focus:text-gray-700 focus:opacity-100 group-hover/name:opacity-100"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                  </div>
                  {(fetchedProductName || productName) && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {fetchedProductName || productName}
                    </p>
                  )}
              </div>
              {showControls && !isEditingName && !isLocalDemoFunnel && (
                <div className="relative">
                  <button
                    ref={buttonRef}
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <MoreHorizontal size={18} className="text-gray-500" />
                  </button>
                  <div
                    ref={dropdownRef}
                    className={`
                      absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10
                      transition-all duration-200 ease-in-out origin-top-right
                      ${showDropdown
                        ? "transform scale-100 opacity-100"
                        : "transform scale-95 opacity-0 pointer-events-none"
                      }
                    `}
                  >
                    <div className="px-4 py-2 text-left text-sm text-gray-500 mb-1 border-b border-gray-200">
                      {dictionary["created"][language]}:{" "}
                      {convertToZone(formatDateToShort(createdAt), "dd/MM/yy")}
                    </div>
                    <button
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setShowDropdown(false);
                        startNameEdit();
                      }}
                    >
                      <Pencil size={14} className="text-gray-500" />
                      {dictionary["rename"][language]}
                    </button>
                    <button
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setShowDropdown(false);
                        handleDuplicate();
                      }}
                    >
                      {dictionary["duplicate"][language]}
                    </button>
                    <button
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                      onClick={() => {
                        setShowDropdown(false);
                        handleOpenModal();
                      }}
                    >
                      {dictionary["delete"][language]}
                    </button>
                  </div>
                </div>
              )}
              {showControls && isEditingName && <div className="h-8 w-8 shrink-0" />}
            </div>
          </div>
        </div>

        <div className="mt-4">
          {funnelSequenceLabel && (
            <p className="mb-1 text-[11px] leading-4 font-bold uppercase tracking-wide text-gray-500">
              {funnelSequenceLabel}
            </p>
          )}
          <p className="text-[13.5px] text-gray-500 font-normal leading-relaxed line-clamp-3">{description}</p>
          {hasSalesSummary && salesSummary && (
            <div className={`mt-3 grid ${hasConversionRate ? "grid-cols-3" : "grid-cols-2"} rounded-xl bg-emerald-50/90 px-3 py-2 text-center text-[11px] leading-4 text-emerald-800`}>
              <div>
                <span className="block font-semibold text-emerald-950">
                  {salesSummary.salesCount.toLocaleString("es-CO")}
                </span>
                <span className="text-emerald-700">Ordenes</span>
              </div>
              <div className={`${hasConversionRate ? "border-x" : "border-l"} border-emerald-200/80 px-2`}>
                <span className="block truncate font-semibold text-emerald-950">
                  {formatCompactCurrency(salesSummary.revenuePaid, salesSummary.currencyCode)}
                </span>
                <span className="text-emerald-700">Facturacion</span>
              </div>
              {conversionRate !== null && (
                <div>
                  <span className="block font-semibold text-emerald-950">
                    {formatConversionRate(conversionRate)}
                  </span>
                  <span className="text-emerald-700">Conversion</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {failed ? (
        <div className="px-6 pb-6">
          {failureReason && (
            <p className="mb-2 line-clamp-2 text-xs text-red-600">
              {failureReason}
            </p>
          )}
          <button
            type="button"
            disabled={isRetrying || !onRetry || (!retryAvailable && (errorSections?.length ?? 0) === 0)}
            onClick={async () => {
              if (!onRetry || isRetrying) return;
              setIsRetrying(true);
              try {
                await onRetry();
              } finally {
                setIsRetrying(false);
              }
            }}
            className="flex items-center justify-center gap-2 w-full h-12 rounded-full px-4 border border-red-200 bg-red-50 text-red-700 text-sm font-semibold hover:bg-red-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <RefreshCw size={16} className={isRetrying ? "animate-spin" : ""} />
            {isRetrying
              ? dictionary["retrying-funnel"][language]
              : dictionary["retry-funnel"][language]}
          </button>
        </div>
      ) : showControls ? (
        <div>
          <div className="px-6 pb-3 overflow-x-hidden">
            <div className="flex space-x-2.5">
              <button
                onClick={handlePreview}
                className="group flex items-center p-3 hover:pr-3.5 text-gray-600 hover:text-gray-800 bg-gray-100 rounded-full transition-all [transition-duration:400ms] ease-in-out hover:bg-gray-200 overflow-hidden"
              >
                <Eye size={19} className="shrink-0" />
                <span className="max-w-0 overflow-hidden whitespace-nowrap text-xs font-medium transition-all [transition-duration:400ms] ease-in-out group-hover:max-w-[4rem] group-hover:ml-1.5">
                  {dictionary["view"][language]}
                </span>
              </button>
              <button
                onClick={handleEdit}
                className="group flex items-center p-3 hover:pr-3.5 text-gray-600 hover:text-gray-800 bg-gray-100 rounded-full transition-all [transition-duration:400ms] ease-in-out hover:bg-gray-200 overflow-hidden"
              >
                <Pencil size={19} className="shrink-0" />
                <span className="max-w-0 overflow-hidden whitespace-nowrap text-xs font-medium transition-all [transition-duration:400ms] ease-in-out group-hover:max-w-[4rem] group-hover:ml-1.5">
                  {dictionary["edit"][language]}
                </span>
              </button>
              <button
                onClick={handleAdsClick}
                className="group flex items-center p-3 hover:pr-3.5 text-gray-600 hover:text-gray-800 bg-gray-100 rounded-full transition-all [transition-duration:400ms] ease-in-out hover:bg-gray-200 overflow-hidden"
              >
                <Megaphone size={19} className="shrink-0" />
                <span className="max-w-0 overflow-hidden whitespace-nowrap text-xs font-medium transition-all [transition-duration:400ms] ease-in-out group-hover:max-w-[5rem] group-hover:ml-1.5">
                  {dictionary["ads"][language]}
                </span>
              </button>
              <Link
                to={`/analytics/${id}`}
                className="group flex items-center p-3 hover:pr-3.5 text-gray-600 hover:text-gray-800 bg-gray-100 rounded-full transition-all [transition-duration:400ms] ease-in-out hover:bg-gray-200 overflow-hidden"
              >
                <LineChart size={19} className="shrink-0" />
                <span className="max-w-0 overflow-hidden whitespace-nowrap text-xs font-medium transition-all [transition-duration:400ms] ease-in-out group-hover:max-w-[5rem] group-hover:ml-1.5">
                  {dictionary["metrics"][language]}
                </span>
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-6 pb-6">
          <div className="flex items-center justify-center gap-3 h-12 bg-white/80 rounded-full px-4 border border-gray-200">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin shrink-0" />
            <span className="text-sm text-gray-500 font-medium">
              {dictionary["your-funnel-will-be-ready-soon"]?.[language] ?? "Your funnel will be ready in a moment"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
